/* eslint-disable @typescript-eslint/prefer-for-of */
import { ApplicationConfig, ApplicationConfigWithPages } from '../app/appConfig.models';
import { PageCascadingFilter, PageConfig, PageConfigWithCharts, PredefinedCharts } from '../page/page.models';
import { BaseChartConfig } from 'shared/models/chart.models';
import { find, forEach } from 'lodash';
import { CONSTANTS } from 'shared/localizations/dictionary';
import { PipeTransform } from '@angular/core';

const SLASH = '/';

export const getCharts = (applications: ApplicationConfigWithPages[]): BaseChartConfig[] => {
  const ret = [];

  forEach(applications, (app: ApplicationConfigWithPages) => {
    if (app.pages) {
      app.pages.forEach(page => {
        if (page.charts) {
          page.charts.forEach(chart => {
            if (chart != null) {
              ret.push(chart);
            }
          });
        }
      });
    }
  });
  return ret;
};

export const getPages = (applications: ApplicationConfigWithPages[]): PageConfigWithCharts[] => {
  const ret = [];
  if (applications) {
    applications.forEach((app: ApplicationConfigWithPages) => {
      if (app.pages) {
        app.pages.forEach(page => {
          if (page != null) {
            ret.push(page);
          }
        });
      }
    });
  }
  return ret;
};

export const getAppConfigs = (applications: ApplicationConfigWithPages[]): ApplicationConfigWithPages[] => {
  const ret = [];

  applications.forEach(app => {
    // delete app['pages'];
    if (app != null) {
      ret.push(app);
    }
  });
  return ret;
};

export const deepCopyObj = (obj: any) => {
  if (!obj) {
    return null;
  }
  return JSON.parse(JSON.stringify(obj));
};

export const getPredefinedTotal = (predefinedCharts: PredefinedCharts[]): number => {
  if (!predefinedCharts || predefinedCharts.length === 0) {
    return 0;
  }
  let totalSelected = 0;
  for (const item of predefinedCharts) {
    for (const chart of item.charts) {
      if (chart.isSelected) {
        totalSelected++;
      }
    }
  }
  return totalSelected;
};

export const getChartsVisibility = (page: PageConfig, charts: BaseChartConfig[]): { [id: string]: boolean } => {
  const ret = {};
  page.chartIds.forEach(chartId => {
    ret[chartId] = true;
  });

  forEach(page.predefinedCharts, item => {
    item.charts.forEach(chart => {
      const found = charts.find(c => c.id === chart.chartId);
      if (found) {
        ret[chart.chartId] = chart.isSelected;
      }
    });
  });
  return ret;
};

export const moveArrayElement = (arr: any[], old_index, new_index) => {
  while (old_index < 0) {
    old_index += arr.length;
  }
  while (new_index < 0) {
    new_index += arr.length;
  }
  if (new_index >= arr.length) {
    let k = new_index - arr.length;
    while (k-- + 1) {
      arr.push(undefined);
    }
  }
  arr.splice(new_index, 0, arr.splice(old_index, 1)[0]);
  return arr;
};

/**
 * Generates valid URLs from application configs
 * NOTE: applications are sidebar menu items in UI
 *
 * Navigation is only possible from sidebar menu as of now
 * So these are the only possible routes app can take
 *
 * @param appConfigs
 * @param childKey
 */
export const generateUrlMap = (appConfigs: ApplicationConfig[]): { [id: string]: ApplicationConfig } => {
  const ret = {};

  if (!appConfigs || appConfigs.length === 0) {
    return ret;
  }

  appConfigs.forEach(app => {
    for (const pageId of app.pageIds) {
      ret[`/applications/${app.dashboardType}/-?[0-9]+/${app.type}/${app.id}/${pageId}/?$`] = app;
      if (app.childPageIds) {
        for (const childPageId1 of app.childPageIds) {
          ret[`/applications/${app.dashboardType}/-?[0-9]+/${app.type}/${app.id}/${pageId}/${childPageId1}/[^/]*/?$`] =
            app;
          for (const childPageId2 of app.childPageIds) {
            ret[
              `/applications/${app.dashboardType}/-?[0-9]+/${app.type}/${app.id}/${pageId}/${childPageId1}/[^/]*/${childPageId2}/[^/]*/?$`
            ] = app;
          }
          for (const childPageId2 of app.childPageIds) {
            ret[
              `/applications/${app.dashboardType}/-?[0-9]+/${app.type}/${app.id}/${pageId}/${childPageId1}/[^/]*/-?[0-9]+/${childPageId2}/[^/]*/?$`
            ] = app;
          }
        }
      }
    }
  });

  if (
    !appConfigs.find(
      app =>
        app.dashboardType === CONSTANTS.DASHBOARD_TYPES.DEVICE ||
        app.dashboardType === CONSTANTS.DASHBOARD_TYPES.WATCHLIST ||
        app.dashboardType === CONSTANTS.DASHBOARD_TYPES.SMARTBATTERY ||
        app.dashboardType === CONSTANTS.DASHBOARD_TYPES.UTILIZATION
    )
  ) {
    ret[CONSTANTS.NO_DASHBOARD_URL] = null;
  }
  return ret;
};

export const generateUrl = (app: ApplicationConfig, profileId): string => {
  if (!app) {
    return null;
  }
  return `${SLASH}applications${SLASH}${app.dashboardType}${SLASH}${profileId}${SLASH}${app.type}${SLASH}${app.id}${SLASH}${app.pageIds[0]}`;
};

export const resolveChart = (charts: BaseChartConfig[], filterChartIdx: string): BaseChartConfig => {
  const filterChartPrefix = 'filterChartIdx-';
  const chartBaselineId = filterChartIdx.substring(filterChartPrefix.length);
  return charts.find(chart => chart.baselineId === chartBaselineId);
};

export const resolveTagLabel = (chart: BaseChartConfig, pipeTransform: PipeTransform): string => {
  if (!chart.tag) {
    return chart.dimension.label;
  }

  let chartName = chart.tag.name.toString();

  if (pipeTransform) {
    chartName = pipeTransform.transform(chartName);
  }

  if (chart.tag.parameters) {
    // Cater for invalid non-array parameter string
    const params: string[] = typeof chart.tag.parameters === 'string' ? [chart.tag.parameters] : chart.tag.parameters;
    params.forEach(param => {
      let paramValue = chart.filters[param];
      if (param != 'mostLeast' && paramValue < 0) {
        paramValue = paramValue * -1;
      }
      // replace user friendly value from uiSettings if exists
      if (chart.uiSettings) {
        const setting = find(chart.uiSettings, x => x.id === param);
        if (setting && setting.options) {
          const option = find(setting.options, x => x.value === paramValue);
          if (option && option.label) {
            paramValue = option.label;
          }
          chartName = chartName.split('{{' + param + '}}').join(pipeTransform.transform(paramValue));
        }
      }
    });
  }
  return chartName;
};

export const resolveSelectionLabel = (chart: BaseChartConfig): string => {
  if (chart.selections[0] === undefined) {
    return null;
  }
  return chart.selections[0].value || null;
};

export const createTag = (
  charts: BaseChartConfig[],
  cascadingFilter: PageCascadingFilter,
  pipeTransform: PipeTransform
) => {
  const chart = resolveChart(charts, cascadingFilter.filterIdx);
  if (!chart) {
    return null;
  }

  const selectionLabel = cascadingFilter.tag
    ? cascadingFilter.tag.filterLabel || `<${cascadingFilter.tag.filterValue}>`
    : resolveSelectionLabel(chart);
  if (!selectionLabel) {
    return null;
  }

  const tagLabel = resolveTagLabel(chart, pipeTransform);
  const selectionValue = cascadingFilter.tag ? cascadingFilter.tag.filterValue : resolveSelectionLabel(chart);

  return {
    label: `${tagLabel}: ${pipeTransform.transform(selectionLabel?.toString())}`,
    selected: false,
    chartId: chart.id,
    chartBaselineId: chart.baselineId,
    index: -1,
    filter: {
      [chart.filter.attribute]: selectionValue,
    },
  };
};
